import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Plus, Trash2, Copy, Download, Printer } from "lucide-react";
import { format, startOfWeek, addDays } from "date-fns";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  summarize,
  formatHours,
  DEFAULT_WAGE,
  type HoursEntry,
  type WageSettings,
} from "@/lib/calculators/hoursCalculator";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type Period = "daily" | "weekly" | "biweekly" | "monthly";

const T = {
  h1: { en: "Hours Calculator", bn: "আওয়ার ক্যালকুলেটর" },
  desc: {
    en: "Calculate work hours with break deduction, overtime, and wages — daily, weekly, bi-weekly or monthly summary.",
    bn: "ব্রেক বাদ দিয়ে কর্মঘণ্টা, ওভারটাইম ও মজুরি গণনা — দৈনিক, সাপ্তাহিক, পাক্ষিক বা মাসিক সারাংশ।",
  },
  date: { en: "Date", bn: "তারিখ" },
  in: { en: "Clock in", bn: "ক্লক ইন" },
  out: { en: "Clock out", bn: "ক্লক আউট" },
  break: { en: "Break (min)", bn: "ব্রেক (মি)" },
  net: { en: "Net hours", bn: "নেট ঘণ্টা" },
  regular: { en: "Regular", bn: "নিয়মিত" },
  ot: { en: "Overtime", bn: "ওভারটাইম" },
  pay: { en: "Pay", bn: "মজুরি" },
  notes: { en: "Notes", bn: "নোট" },
  addRow: { en: "Add row", bn: "সারি যোগ" },
  exportCsv: { en: "Export CSV", bn: "CSV রপ্তানি" },
  print: { en: "Print", bn: "প্রিন্ট" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  rate: { en: "Hourly rate (৳)", bn: "ঘণ্টা প্রতি মজুরি (৳)" },
  otThresh: { en: "Daily OT threshold (h)", bn: "দৈনিক ওটি সীমা (ঘ)" },
  otMult: { en: "OT multiplier", bn: "ওটি গুণক" },
  weekly: { en: "Weekly hours cap", bn: "সাপ্তাহিক সর্বোচ্চ" },
  totalHours: { en: "Total hours", bn: "মোট ঘণ্টা" },
  totalReg: { en: "Regular hours", bn: "নিয়মিত ঘণ্টা" },
  totalOT: { en: "Overtime hours", bn: "ওভারটাইম ঘণ্টা" },
  totalPay: { en: "Total pay", bn: "মোট মজুরি" },
  period: { en: "Period", bn: "সময়কাল" },
  daily: { en: "Daily", bn: "দৈনিক" },
  week: { en: "Weekly", bn: "সাপ্তাহিক" },
  biweek: { en: "Bi-weekly", bn: "পাক্ষিক" },
  month: { en: "Monthly", bn: "মাসিক" },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "How to calculate work hours?", bn: "কর্মঘণ্টা কীভাবে গণনা?" },
    a: {
      en: "Subtract clock-in from clock-out, then deduct unpaid break time. Example: 9:00 AM to 6:30 PM = 9.5 gross hours, minus 1h break = 8.5 net hours.",
      bn: "ক্লক আউট থেকে ক্লক ইন বিয়োগ করে অবৈতনিক ব্রেক বাদ দিন। উদাহরণ: ৯:০০ AM → ৬:৩০ PM = ৯.৫ ঘ গ্রস, ১ ঘ ব্রেক বাদে = ৮.৫ ঘ নেট।",
    },
  },
  {
    q: { en: "What is overtime in Bangladesh?", bn: "বাংলাদেশে ওভারটাইম কী?" },
    a: {
      en: "Per Bangladesh Labour Act 2006, standard hours are 8/day and 48/week. Hours beyond this are overtime, paid at twice the regular rate (2x). Adjust the multiplier above if your contract differs.",
      bn: "বাংলাদেশ শ্রম আইন ২০০৬ অনুযায়ী আদর্শ ৮ ঘণ্টা/দিন ও ৪৮ ঘণ্টা/সপ্তাহ। এর বেশি কাজ ওভারটাইম, যা স্বাভাবিক হারের দ্বিগুণে (২x) দিতে হয়। চুক্তি ভিন্ন হলে গুণক পরিবর্তন করুন।",
    },
  },
  {
    q: { en: "ওভারটাইম কীভাবে গণনা করা হয়?", bn: "ওভারটাইম কীভাবে গণনা করা হয়?" },
    a: {
      en: "OT ঘণ্টা = (নেট ঘণ্টা − দৈনিক সীমা)। ওটি পে = OT ঘণ্টা × রেট × গুণক। সাপ্তাহিক সীমা অতিক্রম করলে অতিরিক্ত নিয়মিত ঘণ্টাও ওটিতে রূপান্তর হয়।",
      bn: "OT ঘণ্টা = (নেট ঘণ্টা − দৈনিক সীমা)। ওটি পে = OT ঘণ্টা × রেট × গুণক। সাপ্তাহিক সীমা অতিক্রম করলে অতিরিক্ত নিয়মিত ঘণ্টাও ওটিতে রূপান্তর হয়।",
    },
  },
  {
    q: { en: "Are breaks paid in Bangladesh?", bn: "ব্রেক কি বেতনের অন্তর্ভুক্ত?" },
    a: {
      en: "Most factories and offices treat the lunch/tiffin break (typically 1 hour) as unpaid. The Labour Act mandates a minimum 1-hour rest in an 8-hour shift. Set break minutes to 0 if your break is paid.",
      bn: "অধিকাংশ কারখানা ও অফিসে দুপুর/টিফিন ব্রেক (সাধারণত ১ ঘণ্টা) অবৈতনিক ধরা হয়। শ্রম আইনে ৮ ঘণ্টা শিফটে কমপক্ষে ১ ঘণ্টা বিশ্রাম বাধ্যতামূলক। ব্রেক বেতনযুক্ত হলে মিনিট 0 দিন।",
    },
  },
  {
    q: { en: "Daily vs weekly overtime — which applies?", bn: "দৈনিক না সাপ্তাহিক ওটি — কোনটি?" },
    a: {
      en: "We apply daily OT first (anything over 8h/day). Then if your weekly regular hours still exceed the weekly cap (48h by default), the extra is reclassified as OT. The higher of the two applies — you never lose OT pay.",
      bn: "প্রথমে দৈনিক ওটি (৮ ঘ/দিনের বেশি) প্রয়োগ করি। তারপর সপ্তাহের নিয়মিত ঘণ্টা সীমা (ডিফল্ট ৪৮) অতিক্রম করলে অতিরিক্ত ওটিতে রূপান্তর হয়। বেশি যেটা — সেটাই গণনা হয়।",
    },
  },
  {
    q: { en: "Is my data saved?", bn: "তথ্য কি সেভ হয়?" },
    a: {
      en: "Yes — entries auto-save in your browser (localStorage). Use 'Export CSV' to download or 'Print' for a payroll record. Nothing is uploaded to a server.",
      bn: "হ্যাঁ — সকল এন্ট্রি স্বয়ংক্রিয়ভাবে আপনার ব্রাউজারে (localStorage) সেভ হয়। 'CSV রপ্তানি' বা 'প্রিন্ট' করে রেকর্ড রাখুন। কোনো ডেটা সার্ভারে যায় না।",
    },
  },
];

export const Route = createFileRoute("/hours-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Hours Calculator — Work Hours, Overtime, Wage",
      description:
        "Free work hours calculator. Calculate daily/weekly hours, overtime, wages. Includes break deduction. Per Bangladesh Labour Act standards. আওয়ার ক্যালকুলেটর।",
      path: "/hours-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Hours Calculator", path: "/hours-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: HoursCalculatorPage,
});

const STORAGE_KEY = "bc.hours.v1";

interface RowState extends HoursEntry {
  id: string;
}

function defaultRows(): RowState[] {
  const monday = startOfWeek(new Date(), { weekStartsOn: 0 });
  return [0, 1, 2, 3, 4].map((i) => ({
    id: Math.random().toString(36).slice(2, 9),
    date: format(addDays(monday, i), "yyyy-MM-dd"),
    clockIn: "09:00",
    clockOut: "18:00",
    breakMinutes: 60,
    notes: "",
  }));
}

function HoursCalculatorPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmtN = (n: number, dp = 2) =>
    num(n.toLocaleString("en-US", { maximumFractionDigits: dp }));
  const fmtH = (h: number) => (useBn ? toBengaliNumerals(formatHours(h)) : formatHours(h));

  const [rows, setRows] = useState<RowState[]>(defaultRows);
  const [wage, setWage] = useState<WageSettings>({ ...DEFAULT_WAGE, ratePerHour: 200 });
  const [period, setPeriod] = useState<Period>("weekly");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.rows) && parsed.rows.length) setRows(parsed.rows);
        if (parsed.wage) setWage({ ...DEFAULT_WAGE, ...parsed.wage });
        if (parsed.period) setPeriod(parsed.period);
      }
    } catch {}
    setHydrated(true);
  }, []);
  useEffect(() => {
    if (!hydrated || typeof window === "undefined") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ rows, wage, period }));
    } catch {}
  }, [rows, wage, period, hydrated]);

  const summary = useMemo(() => {
    const applyWeekly = period !== "daily";
    return summarize(rows, wage, applyWeekly);
  }, [rows, wage, period]);

  const update = (i: number, patch: Partial<RowState>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const addRow = () => {
    const last = rows[rows.length - 1];
    const nextDate = last ? format(addDays(new Date(last.date), 1), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");
    setRows((rs) => [
      ...rs,
      {
        id: Math.random().toString(36).slice(2, 9),
        date: nextDate,
        clockIn: "09:00",
        clockOut: "18:00",
        breakMinutes: 60,
        notes: "",
      },
    ]);
  };

  const dupRow = (i: number) => {
    const r = rows[i];
    setRows((rs) => [
      ...rs.slice(0, i + 1),
      { ...r, id: Math.random().toString(36).slice(2, 9) },
      ...rs.slice(i + 1),
    ]);
  };

  const removeRow = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const exportCsv = () => {
    const header = "Date,Clock In,Clock Out,Break (min),Net Hours,Regular,Overtime,Pay,Notes\n";
    const body = rows
      .map((r, i) => {
        const res = summary.rowResults[i];
        return [
          r.date,
          r.clockIn,
          r.clockOut,
          r.breakMinutes,
          res.netHours.toFixed(2),
          res.regularHours.toFixed(2),
          res.overtimeHours.toFixed(2),
          res.totalPay.toFixed(2),
          `"${(r.notes ?? "").replace(/"/g, '""')}"`,
        ].join(",");
      })
      .join("\n");
    const totals = `\nTOTAL,,,,${summary.totalNet.toFixed(2)},${summary.totalRegular.toFixed(2)},${summary.totalOvertime.toFixed(2)},${summary.totalPay.toFixed(2)},`;
    const blob = new Blob([header + body + totals], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hours-timesheet.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const doPrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <CalculatorLayout
      slug="hours-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Timer"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Top controls */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-border bg-card p-1 text-xs">
          {(["daily", "weekly", "biweekly", "monthly"] as Period[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={cn(
                "rounded-full px-3 py-1.5 font-medium transition-colors",
                period === p ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
                lang === "bn" && "font-bn",
              )}
            >
              {p === "daily" ? tr("daily") : p === "weekly" ? tr("week") : p === "biweekly" ? tr("biweek") : tr("month")}
            </button>
          ))}
        </div>
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      {/* Wage settings */}
      <div className="mb-4 grid gap-3 rounded-2xl border border-border bg-muted/30 p-4 md:grid-cols-4">
        <NumField label={tr("rate")} value={String(wage.ratePerHour)} onChange={(v) => setWage((w) => ({ ...w, ratePerHour: parseFloat(v) || 0 }))} useBn={useBn} lang={lang} />
        <NumField label={tr("otThresh")} value={String(wage.dailyOTThreshold)} onChange={(v) => setWage((w) => ({ ...w, dailyOTThreshold: parseFloat(v) || 0 }))} useBn={useBn} lang={lang} />
        <NumField label={tr("otMult")} value={String(wage.otMultiplier)} onChange={(v) => setWage((w) => ({ ...w, otMultiplier: parseFloat(v) || 1 }))} useBn={useBn} lang={lang} />
        <NumField label={tr("weekly")} value={String(wage.weeklyHours)} onChange={(v) => setWage((w) => ({ ...w, weeklyHours: parseFloat(v) || 0 }))} useBn={useBn} lang={lang} />
      </div>

      {/* Timesheet table */}
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-2 text-left">{tr("date")}</th>
              <th className="p-2 text-left">{tr("in")}</th>
              <th className="p-2 text-left">{tr("out")}</th>
              <th className="p-2 text-right">{tr("break")}</th>
              <th className="p-2 text-right">{tr("net")}</th>
              <th className="p-2 text-right">{tr("regular")}</th>
              <th className="p-2 text-right">{tr("ot")}</th>
              <th className="p-2 text-right">{tr("pay")}</th>
              <th className="p-2 print:hidden"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const res = summary.rowResults[i];
              return (
                <tr key={r.id} className="border-t border-border align-middle">
                  <td className="p-2">
                    <input type="date" value={r.date} onChange={(e) => update(i, { date: e.target.value })} className="rounded border border-border bg-background px-2 py-1 text-sm outline-none focus:border-accent" />
                  </td>
                  <td className="p-2">
                    <input type="time" value={r.clockIn} onChange={(e) => update(i, { clockIn: e.target.value })} className="w-24 rounded border border-border bg-background px-2 py-1 text-sm tabular-nums outline-none focus:border-accent" />
                  </td>
                  <td className="p-2">
                    <input type="time" value={r.clockOut} onChange={(e) => update(i, { clockOut: e.target.value })} className="w-24 rounded border border-border bg-background px-2 py-1 text-sm tabular-nums outline-none focus:border-accent" />
                  </td>
                  <td className="p-2 text-right">
                    <input
                      inputMode="numeric"
                      value={useBn ? toBengaliNumerals(r.breakMinutes) : String(r.breakMinutes)}
                      onChange={(e) =>
                        update(i, {
                          breakMinutes: parseInt(
                            e.target.value
                              .replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d)))
                              .replace(/[^0-9]/g, ""),
                            10,
                          ) || 0,
                        })
                      }
                      className="w-16 rounded border border-border bg-background px-2 py-1 text-right tabular-nums outline-none focus:border-accent"
                    />
                  </td>
                  <td className="p-2 text-right tabular-nums">{fmtH(res.netHours)}</td>
                  <td className="p-2 text-right tabular-nums text-muted-foreground">{fmtH(res.regularHours)}</td>
                  <td className={cn("p-2 text-right tabular-nums", res.overtimeHours > 0 ? "text-accent" : "text-muted-foreground")}>{fmtH(res.overtimeHours)}</td>
                  <td className="p-2 text-right font-medium tabular-nums">৳{fmtN(res.totalPay)}</td>
                  <td className="p-2 print:hidden">
                    <div className="inline-flex gap-1">
                      <button type="button" onClick={() => dupRow(i)} className="rounded p-1 text-muted-foreground hover:text-accent" aria-label="Duplicate">
                        <Copy size={14} />
                      </button>
                      <button type="button" onClick={() => removeRow(i)} className="rounded p-1 text-muted-foreground hover:text-destructive" aria-label="Remove">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-accent/5">
            <tr className="border-t-2 border-accent/30 font-semibold">
              <td className="p-2" colSpan={4}>{lang === "en" ? "Total" : "মোট"}</td>
              <td className="p-2 text-right tabular-nums">{fmtH(summary.totalNet)}</td>
              <td className="p-2 text-right tabular-nums">{fmtH(summary.totalRegular)}</td>
              <td className="p-2 text-right tabular-nums">{fmtH(summary.totalOvertime)}</td>
              <td className="p-2 text-right tabular-nums">৳{fmtN(summary.totalPay)}</td>
              <td className="print:hidden"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 print:hidden">
        <button type="button" onClick={addRow} className={cn("inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-xs font-medium hover:border-accent", lang === "bn" && "font-bn")}>
          <Plus size={12} /> {tr("addRow")}
        </button>
        <button type="button" onClick={exportCsv} className={cn("inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-xs font-medium hover:border-accent", lang === "bn" && "font-bn")}>
          <Download size={12} /> {tr("exportCsv")}
        </button>
        <button type="button" onClick={doPrint} className={cn("inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-xs font-medium hover:border-accent", lang === "bn" && "font-bn")}>
          <Printer size={12} /> {tr("print")}
        </button>
      </div>

      {/* Summary cards */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
        <SummaryCard label={tr("totalHours")} value={fmtH(summary.totalNet)} />
        <SummaryCard label={tr("totalReg")} value={fmtH(summary.totalRegular)} />
        <SummaryCard label={tr("totalOT")} value={fmtH(summary.totalOvertime)} accent />
        <SummaryCard label={tr("totalPay")} value={`৳${fmtN(summary.totalPay)}`} accent />
      </motion.div>

      {summary.weeklyOvertime > 0 && (
        <p className={cn("mt-3 rounded-lg border border-accent/40 bg-accent/5 p-3 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          {lang === "en"
            ? `Weekly cap exceeded by ${formatHours(summary.weeklyOvertime)} — additional hours reclassified as overtime.`
            : `সাপ্তাহিক সীমা ${useBn ? toBengaliNumerals(formatHours(summary.weeklyOvertime)) : formatHours(summary.weeklyOvertime)} অতিক্রম — অতিরিক্ত ঘণ্টা ওভারটাইম হিসেবে গণনা।`}
        </p>
      )}

      {/* Educational */}
      <article className="mt-12 space-y-10 print:hidden">
        <Section title={lang === "en" ? "How work hours are calculated" : "কর্মঘণ্টা কীভাবে গণনা"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Net hours = (clock-out − clock-in) − unpaid break. We then split into regular hours (up to your daily threshold) and overtime hours (anything beyond). Pay = regular × rate + overtime × rate × multiplier."
              : "নেট ঘণ্টা = (ক্লক আউট − ক্লক ইন) − অবৈতনিক ব্রেক। তারপর নিয়মিত (দৈনিক সীমা পর্যন্ত) ও ওভারটাইম (অতিরিক্ত) হিসেবে ভাগ। মজুরি = নিয়মিত × রেট + ওটি × রেট × গুণক।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Bangladesh Labour Act 2006" : "বাংলাদেশ শ্রম আইন ২০০৬"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Standard working hours are 8 per day and 48 per week (factory & service sectors). Overtime beyond this is paid at 2× the regular hourly rate. The Act also mandates one weekly holiday and a minimum 1-hour break in any 8-hour shift. Adjust the multiplier and weekly cap above to match your contract."
              : "আদর্শ কর্মঘণ্টা ৮/দিন ও ৪৮/সপ্তাহ (কারখানা ও সেবা খাত)। এর বেশি কাজ স্বাভাবিক হারের ২× হিসেবে দিতে হয়। প্রতি সপ্তাহে ১ দিন ছুটি ও ৮ ঘণ্টা শিফটে ১ ঘণ্টা বিশ্রাম বাধ্যতামূলক। আপনার চুক্তি অনুযায়ী গুণক ও সাপ্তাহিক সীমা সমন্বয় করুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Paid vs unpaid breaks" : "বেতনযুক্ত বনাম অবৈতনিক ব্রেক"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Most BD garment factories and offices treat the 1-hour lunch as unpaid (deducted from net hours). Tea/tiffin breaks (10–15 min) are usually paid. If your break is paid, set the break minutes to 0."
              : "অধিকাংশ পোশাক কারখানা ও অফিসে ১ ঘণ্টা দুপুরের ব্রেক অবৈতনিক (নেট ঘণ্টা থেকে বাদ)। চা/টিফিন ব্রেক (১০–১৫ মি) সাধারণত বেতনযুক্ত। ব্রেক বেতনযুক্ত হলে মিনিট 0 দিন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Use cases" : "ব্যবহারের ক্ষেত্র"} lang={lang}>
          <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Garment & RMG factory shift workers — track daily/weekly OT" : "পোশাক ও RMG কারখানার শিফট কর্মী — দৈনিক/সাপ্তাহিক ওটি"}</li>
            <li>{lang === "en" ? "Freelancers — bill clients by tracked hours" : "ফ্রিল্যান্সার — ট্র্যাক করা ঘণ্টা অনুযায়ী বিল"}</li>
            <li>{lang === "en" ? "Retail & restaurant staff — multiple shifts, overnight closes" : "রিটেইল ও রেস্টুরেন্ট কর্মী — একাধিক শিফট, রাতের ক্লোজ"}</li>
            <li>{lang === "en" ? "Office employees — verify HR-calculated OT" : "অফিস কর্মী — HR-এর ওটি যাচাই"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/time-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Time" : "সময়"}</Link>
            <Link to="/days-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Days" : "দিন"}</Link>
            <Link to="/date-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Date" : "তারিখ"}</Link>
            <Link to="/tax-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Tax" : "ট্যাক্স"}</Link>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function SummaryCard({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={cn("rounded-xl border p-3", accent ? "border-accent/40 bg-accent/5" : "border-border bg-card")}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function NumField({
  label, value, onChange, lang, useBn,
}: { label: string; value: string; onChange: (v: string) => void; lang: Lang; useBn: boolean }) {
  const display = useBn ? toBengaliNumerals(value) : value;
  return (
    <div className="space-y-1">
      <label className={cn("block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{label}</label>
      <input
        type="text"
        inputMode="decimal"
        value={display}
        onChange={(e) =>
          onChange(
            e.target.value
              .replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d)))
              .replace(/[^0-9.]/g, ""),
          )
        }
        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm tabular-nums outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
      />
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: Lang }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
